import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Spinner from "../common/Spinner";
import {
  createQuote,
  getQuote,
  updateQuote,
  sendQuoteToCustomer,
  openQuotePdf,
  createQuotePaymentLink,
  listCustomerCredits,
} from "../../api/quotes";
import { searchCustomers, createCustomer } from "../../api/customers";
import { fetchServices } from "../../api/services";
import { formatUSD } from "../../utils/money";
import { TaxCountyPicker } from "../common/TaxCountyPicker";
import VenueAutocomplete from "../common/VenueAutocomplete";
import {
  packagesFrom,
  packageLineDescription,
  NOTE_BLOCKS,
  hasNoteBlock,
  addNoteBlock,
} from "../../utils/quoteTemplates";

const BLANK_ITEM = { description: "", quantity: 1, unit_label: "", unit_price: "", taxable: true };

function centsFromInput(value) {
  const n = parseFloat(value);
  return Number.isNaN(n) ? 0 : Math.round(n * 100);
}

function inputFromCents(cents) {
  if (cents === null || cents === undefined || cents === "") return "";
  return (Number(cents) / 100).toFixed(2);
}

function toDateTimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Default hold length when the owner hasn't set an end time yet.
const DEFAULT_EVENT_HOURS = 4;

// Add hours to a `datetime-local` string, returning the same local format.
function addHoursToLocal(local, hours) {
  if (!local) return "";
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return "";
  d.setHours(d.getHours() + hours);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const pad2 = (n) => String(n).padStart(2, "0");

// Times only ever land on the quarter hour, so offer those and nothing else.
const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 0; h < 24; h += 1) {
    for (const m of [0, 15, 30, 45]) {
      const value = `${pad2(h)}:${pad2(m)}`;
      const hour12 = ((h + 11) % 12) + 1;
      const label = `${hour12}:${pad2(m)} ${h < 12 ? "AM" : "PM"}`;
      opts.push({ value, label });
    }
  }
  return opts;
})();

// A `datetime-local` string ("YYYY-MM-DDTHH:mm") split into / rebuilt from parts.
function splitLocal(local) {
  if (!local || !local.includes("T")) return { date: "", time: "" };
  const [date, rest = ""] = local.split("T");
  return { date, time: rest.slice(0, 5) };
}
function combineLocal(date, time) {
  // A time with no date isn't a usable event start, so require the date.
  if (!date) return "";
  return `${date}T${time || "09:00"}`;
}

// Normalize whatever the API hands back into a <input type="date"> value.
// Dates now arrive ISO, but a cached bundle may still see Flask's old RFC-822
// form ("Wed, 14 Oct 2026 00:00:00 GMT") — slicing that to 10 chars yields
// "Wed, 14 Oc", which the database rejects on save.
function toDateInput(value) {
  if (!value) return "";
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  // RFC-822 dates are UTC midnight; read UTC parts or the day shifts back one.
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

// Local YYYY-MM-DD for `days` from today, used as the default quote expiry.
// Built from local date parts so it doesn't shift a day across time zones.
function dateInDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// `prefill` and `onSaved` are optional and only used when a quote is started
// from a lead: the lead's customer and title seed a new quote, the enquiry is
// shown for reference (never saved, never on the PDF), and the inbox is told
// which quote it became. Without them the builder behaves as before.
// `pendingChanges` (optional) is a customer's request from their quote page:
// the add-ons and/or upgrade they asked for are added to the form once the
// quote loads, for you to check and then Save & send as usual.
export default function QuoteBuilder({ quoteId, onClose, prefill = null, onSaved, pendingChanges = null }) {
  const [loading, setLoading] = useState(Boolean(quoteId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  // A double-book is informational (the hold is placed anyway); we warn once,
  // then let the next Save/Send close through.
  const [conflictAck, setConflictAck] = useState(false);
  const [savedId, setSavedId] = useState(quoteId || null);
  const [status, setStatus] = useState("draft");
  // Square payment state, surfaced once the quote exists.
  const [payment, setPayment] = useState({
    status: null,
    squareUrl: "",
    publicToken: "",
    working: false,
  });

  const [customer, setCustomer] = useState(quoteId ? null : prefill?.customer || null);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [newCustomer, setNewCustomer] = useState(null); // null = form closed
  const [creatingCustomer, setCreatingCustomer] = useState(false);

  const [form, setForm] = useState({
    title: (!quoteId && prefill?.title) || "",
    event_date: "",
    event_end: "",
    event_location: (!quoteId && prefill?.event_location) || "",
    notes: "",
    terms: "",
    discount: "",
    discount_reason: "",
    deposit: "",
    // New quotes default to a 30-day expiry; editable, and overwritten by the
    // saved value when an existing quote is loaded below.
    valid_until: dateInDays(30),
    tax_county: "",
    tax_rate_basis_points: null,
    credit_id: null,
  });
  const [items, setItems] = useState([{ ...BLANK_ITEM }]);
  const [services, setServices] = useState([]);
  const [servicesLoaded, setServicesLoaded] = useState(false);
  const [credits, setCredits] = useState([]);
  // "Custom title" is chosen explicitly, or implied by a title that isn't a package.
  const [customTitle, setCustomTitle] = useState(false);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Stable identity: TaxCountyPicker keys a debounce effect off this callback.
  const handleTaxChange = useCallback(({ county, rateBasisPoints }) => {
    setForm((f) => ({
      ...f,
      tax_county: county || "",
      tax_rate_basis_points: rateBasisPoints ?? null,
    }));
  }, []);

  // Load service catalog so line items can be added with one click.
  useEffect(() => {
    fetchServices({ includeInactive: false })
      .then((res) => setServices(Array.isArray(res) ? res : []))
      .catch(() => {})
      .finally(() => setServicesLoaded(true));
  }, []);

  // Load an existing quote for editing.
  useEffect(() => {
    if (!quoteId) return;
    let active = true;
    getQuote(quoteId)
      .then((q) => {
        if (!active) return;
        setStatus(q.status);
        setPayment({
          status: q.payment_status || null,
          squareUrl: q.square_payment_link_url || "",
          publicToken: q.public_token || "",
          working: false,
        });
        setCustomer({
          id: q.customer_id,
          full_name: q.customer_name,
          email: q.customer_email,
          company_name: q.customer_company,
        });
        setForm({
          title: q.title || "",
          event_date: toDateTimeLocal(q.event_date),
          event_end: toDateTimeLocal(q.event_end),
          event_location: q.event_location || "",
          notes: q.notes || "",
          terms: q.terms || "",
          discount: inputFromCents(q.discount_amount_cents || ""),
          discount_reason: q.discount_reason || "",
          deposit: inputFromCents(q.deposit_cents),
          valid_until: toDateInput(q.valid_until) || dateInDays(30),
          tax_county: q.tax_county || "",
          tax_rate_basis_points: q.tax_rate_basis_points ?? null,
          credit_id: q.credit_id || null,
        });
        setItems(
          (q.line_items || []).length
            ? q.line_items.map((li) => ({
                description: li.description || "",
                quantity: Number(li.quantity) || 1,
                unit_label: li.unit_label || "",
                unit_price: inputFromCents(li.unit_price_cents),
                taxable: li.taxable !== false,
              }))
            : [{ ...BLANK_ITEM }]
        );
      })
      .catch((e) => active && setError(e.message || "Could not load quote."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [quoteId]);

  // Rain credits this customer can put toward the quote.
  const customerId = customer?.id;
  useEffect(() => {
    if (!customerId) {
      setCredits([]);
      return;
    }
    let active = true;
    listCustomerCredits(customerId, savedId)
      .then((rows) => active && setCredits(Array.isArray(rows) ? rows : []))
      .catch(() => active && setCredits([]));
    return () => {
      active = false;
    };
  }, [customerId, savedId]);

  const packages = useMemo(() => packagesFrom(services), [services]);
  const selectedPackage = customTitle
    ? null
    : packages.find((p) => p.name === form.title) || null;

  // Choosing a package fills the title, its line item (price included) and
  // the end time. Re-choosing swaps the package line rather than adding one.
  function choosePackage(pkg) {
    setCustomTitle(false);
    const line = {
      ...BLANK_ITEM,
      description: packageLineDescription(pkg),
      unit_price: inputFromCents(pkg.price_cents),
    };
    const pkgLines = new Set(packages.map(packageLineDescription));
    setItems((prev) => {
      const at = prev.findIndex((it) => pkgLines.has(it.description));
      if (at >= 0) return prev.map((it, i) => (i === at ? line : it));
      if (prev.length === 1 && !prev[0].description.trim() && !prev[0].unit_price) return [line];
      return [line, ...prev];
    });
    setForm((f) => ({
      ...f,
      title: pkg.name,
      event_end: f.event_date ? addHoursToLocal(f.event_date, pkg.duration_minutes / 60) : f.event_end,
    }));
  }

  // Apply a customer's request once: after the quote has loaded (and the
  // packages, if they asked for an upgrade). Nothing is saved until you do.
  const pendingApplied = useRef(false);
  const [pendingMissed, setPendingMissed] = useState([]);
  useEffect(() => {
    if (!pendingChanges || pendingApplied.current || loading || !servicesLoaded) return;
    pendingApplied.current = true;
    const missed = [];
    for (const item of pendingChanges.items || []) {
      if (item.kind === "upgrade") {
        const pkg = packages.find((p) => String(p.id) === String(item.package_id));
        if (pkg) choosePackage(pkg);
        else missed.push(item.name);
      } else {
        setItems((prev) => {
          if (prev.some((it) => it.description === item.name)) return prev;
          const line = { ...BLANK_ITEM, description: item.name, unit_price: inputFromCents(item.price_cents) };
          const blank = prev.length === 1 && !prev[0].description.trim() && !prev[0].unit_price;
          return blank ? [line] : [...prev, line];
        });
      }
    }
    setPendingMissed(missed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingChanges, loading, servicesLoaded, packages]);

  // Debounced customer search.
  useEffect(() => {
    if (customer || customerQuery.trim().length < 2) {
      setCustomerResults([]);
      return;
    }
    let active = true;
    setSearching(true);
    const t = setTimeout(() => {
      searchCustomers(customerQuery.trim(), 8)
        .then((rows) => active && setCustomerResults(Array.isArray(rows) ? rows : []))
        .catch(() => active && setCustomerResults([]))
        .finally(() => active && setSearching(false));
    }, 250);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [customerQuery, customer]);

  // Open the inline create form, seeding the name from whatever was typed in
  // the search box so a "no results" search flows straight into creating.
  function openNewCustomerForm() {
    const typed = customerQuery.trim();
    setNewCustomer({
      full_name: typed.includes("@") ? "" : typed,
      email: typed.includes("@") ? typed : "",
      phone: "",
      company_name: "",
    });
    setCustomerResults([]);
    setError("");
  }

  async function handleCreateCustomer() {
    const draft = newCustomer;
    if (!draft.full_name.trim()) {
      setError("Customer name is required.");
      return;
    }
    if (!draft.phone.trim()) {
      setError("Phone number is required.");
      return;
    }
    setCreatingCustomer(true);
    setError("");
    try {
      const created = await createCustomer({
        full_name: draft.full_name.trim(),
        email: draft.email.trim() || null,
        phone: draft.phone.trim(),
        company_name: draft.company_name.trim() || null,
      });
      setCustomer(created);
      setNewCustomer(null);
      setCustomerQuery("");
    } catch (e) {
      setError(e.message || "Could not create the customer.");
    } finally {
      setCreatingCustomer(false);
    }
  }

  const updateItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const addItem = (preset) => setItems((prev) => [...prev, { ...BLANK_ITEM, ...(preset || {}) }]);

  const removeItem = (idx) =>
    setItems((prev) => (prev.length === 1 ? [{ ...BLANK_ITEM }] : prev.filter((_, i) => i !== idx)));

  // Mirrors the server-side computation so the preview matches the PDF.
  const totals = useMemo(() => {
    let subtotal = 0;
    let taxable = 0;
    for (const it of items) {
      const amount = Math.round((Number(it.quantity) || 0) * centsFromInput(it.unit_price));
      subtotal += amount;
      if (it.taxable !== false) taxable += amount;
    }
    const discount = centsFromInput(form.discount);
    const rateBp = Number(form.tax_rate_basis_points ?? 0);
    const taxBase = Math.max(0, taxable - Math.min(discount, taxable));
    const tax = Math.round((taxBase * rateBp) / 10000);
    const beforeCredit = Math.max(0, subtotal - discount) + tax;
    const credit = credits.find((c) => c.id === form.credit_id);
    const creditApplied = credit ? Math.min(credit.available_cents, beforeCredit) : 0;
    return {
      subtotal,
      discount,
      tax,
      creditApplied,
      total: beforeCredit - creditApplied,
    };
  }, [items, form.discount, form.tax_rate_basis_points, form.credit_id, credits]);

  const buildPayload = useCallback(
    () => ({
      customer_id: customer?.id,
      title: form.title,
      event_date: form.event_date ? new Date(form.event_date).toISOString() : null,
      event_end: form.event_end ? new Date(form.event_end).toISOString() : null,
      event_location: form.event_location,
      notes: form.notes,
      terms: form.terms,
      discount_amount_cents: centsFromInput(form.discount),
      discount_reason: form.discount_reason,
      deposit_cents: form.deposit ? centsFromInput(form.deposit) : null,
      valid_until: form.valid_until || null,
      tax_county: form.tax_county,
      tax_rate_basis_points: form.tax_rate_basis_points,
      credit_id: form.credit_id || null,
      line_items: items
        .filter((it) => it.description.trim())
        .map((it) => ({
          description: it.description,
          quantity: Number(it.quantity) || 1,
          unit_label: it.unit_label,
          unit_price_cents: centsFromInput(it.unit_price),
          taxable: it.taxable !== false,
        })),
    }),
    [customer, form, items]
  );

  const save = useCallback(async () => {
    if (!customer?.id) {
      setError("Pick a customer first.");
      return null;
    }
    const payload = buildPayload();
    if (payload.line_items.length === 0) {
      setError("Add at least one line item.");
      return null;
    }
    setError("");
    const saved = savedId
      ? await updateQuote(savedId, payload)
      : await createQuote(payload);
    setSavedId(saved.id);
    onSaved?.(saved);
    setStatus(saved.status);
    setPayment((p) => ({
      ...p,
      status: saved.payment_status ?? p.status,
      // Server is authoritative: an edit can retire a link, and showing a dead
      // URL would invite copying one that no longer charges the right amount.
      squareUrl: saved.square_payment_link_url || "",
      publicToken: saved.public_token || p.publicToken,
    }));

    // A price change retires any payment link built from the old total.
    if (saved.payment_links_invalidated) {
      setNotice(
        "The amount changed, so the old payment link was retired. Create a new " +
          "one before sending — Save & send does this for you."
      );
      return saved;
    }

    // Reflect the soft-hold that the backend placed on the calendar.
    const sync = saved.booking_sync;
    if (!sync?.conflict) setConflictAck(false); // reset once the clash is gone
    if (sync?.conflict) {
      const c = sync.conflict;
      setNotice(
        `Heads up: this time overlaps another booking (${new Date(
          c.start_at
        ).toLocaleString()} – ${new Date(
          c.end_at
        ).toLocaleTimeString()}). The hold was still placed on your calendar.`
      );
    } else if (sync?.calendar === "created") {
      setNotice("Pencilled onto your calendar as a soft hold.");
    } else if (sync?.calendar === "error") {
      setNotice("Saved, but the calendar sync failed — check Google Calendar settings.");
    } else {
      setNotice("");
    }
    return saved;
  }, [buildPayload, customer, savedId, onSaved]);

  // True when the save can close through: it succeeded, and either there's no
  // double-book or the owner has already been shown the warning once.
  function canCloseAfter(saved) {
    if (!saved) return false;
    if (!saved.booking_sync?.conflict) return true;
    if (conflictAck) return true;
    setConflictAck(true); // warn once; the banner from save() is already showing
    return false;
  }

  async function handleSaveDraft() {
    setSaving(true);
    try {
      const saved = await save();
      if (canCloseAfter(saved)) onClose?.(true);
    } catch (e) {
      setError(e.message || "Could not save quote.");
    } finally {
      setSaving(false);
    }
  }

  async function handleGeneratePaymentLink() {
    setError("");
    setPayment((p) => ({ ...p, working: true }));
    try {
      // Save any pending edits first so the link bills the current total.
      const saved = await save();
      if (!saved) return;
      const res = await createQuotePaymentLink(saved.id);
      setPayment((p) => ({
        ...p,
        status: res.quote?.payment_status ?? "unpaid",
        squareUrl: res.payment_url || p.squareUrl,
        publicToken: res.quote?.public_token || p.publicToken,
      }));
    } catch (e) {
      setError(e.message || "Could not create the payment link.");
    } finally {
      setPayment((p) => ({ ...p, working: false }));
    }
  }

  async function handlePreview() {
    setSaving(true);
    try {
      const saved = await save();
      if (saved) await openQuotePdf(saved.id);
    } catch (e) {
      setError(e.message || "Could not build PDF.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSend() {
    setSaving(true);
    try {
      const saved = await save();
      if (!saved) return;
      // On a first-seen double-book, hold off sending so the warning is seen.
      if (saved.booking_sync?.conflict && !conflictAck) {
        setConflictAck(true);
        return;
      }
      await sendQuoteToCustomer(saved.id);
      onClose?.(true);
    } catch (e) {
      setError(e.message || "Could not send quote.");
    } finally {
      setSaving(false);
    }
  }

  const readOnly = status === "signed";

  return (
    <Modal
      isOpen
      title={savedId ? "Edit Quote" : "New Quote"}
      onClose={() => onClose?.(false)}
      maxWidthClass="max-w-none w-[95vw] xl:w-[1100px]"
      heightClass="max-h-[92vh]"
    >
      {loading ? (
        <div className="py-16 flex justify-center">
          <Spinner label="Loading quote..." />
        </div>
      ) : (
        <div className="space-y-6">
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}
          {!quoteId && prefill?.reference && (
            <div className="rounded-xl border border-birdie-green/20 bg-birdie-cream p-4 text-sm text-birdie-charcoal">
              <div className="text-xs font-semibold uppercase tracking-wide text-birdie-charcoal/50 mb-1">
                From the enquiry — for reference, not on the quote
              </div>
              <div className="whitespace-pre-line">{prefill.reference}</div>
            </div>
          )}
          {pendingChanges && (
            <div className="rounded-xl border border-birdie-mustard/50 bg-birdie-mustard/10 p-4 text-sm text-birdie-charcoal">
              <div className="font-semibold">
                🛎️ {customer?.full_name?.split(" ")[0] || "The customer"} asked for:{" "}
                {(pendingChanges.items || []).map((i) => i.name).join(", ")}
              </div>
              {pendingChanges.note && <div className="mt-1 italic">"{pendingChanges.note}"</div>}
              <div className="text-xs text-birdie-charcoal/65 mt-1.5">
                Added below. Check the prices, then <strong>Save &amp; send</strong> to email the updated quote and
                payment link.
                {pendingMissed.length > 0 && (
                  <span className="block text-red-700 mt-1">
                    Couldn't add automatically: {pendingMissed.join(", ")}. Choose it in Package above.
                  </span>
                )}
              </div>
            </div>
          )}
          {notice && !error && (
            <div className="rounded-xl border border-birdie-mustard/40 bg-birdie-mustard/10 p-4 text-sm text-birdie-charcoal">
              {notice}
            </div>
          )}
          {readOnly && (
            <div className="rounded-xl border border-birdie-green/25 bg-birdie-green/5 p-4 text-sm text-birdie-green">
              This quote has been signed and can no longer be edited.
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: details + line items */}
            <div className="lg:col-span-2 space-y-6">
              <Section title="Customer">
                {customer ? (
                  <div className="flex items-center justify-between gap-3 bg-birdie-cream border border-birdie-green/20 rounded-xl p-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-birdie-charcoal truncate">
                        {customer.full_name}
                      </div>
                      <div className="text-sm text-birdie-charcoal/70 truncate">
                        {customer.email || "no email on file"}
                        {customer.company_name ? ` · ${customer.company_name}` : ""}
                      </div>
                    </div>
                    {!readOnly && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setCustomer(null);
                          setCustomerQuery("");
                        }}
                      >
                        Change
                      </Button>
                    )}
                  </div>
                ) : newCustomer ? (
                  <div className="bg-birdie-cream border border-birdie-green/20 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-birdie-charcoal text-sm">New customer</h4>
                      <Button variant="ghost" size="sm" onClick={() => setNewCustomer(null)}>
                        Back to search
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field label="Full name *">
                        <input
                          value={newCustomer.full_name}
                          onChange={(e) =>
                            setNewCustomer((c) => ({ ...c, full_name: e.target.value }))
                          }
                          placeholder="Sarah O'Brien"
                          className={inputCls}
                          autoFocus
                        />
                      </Field>
                      <Field label="Phone *">
                        <input
                          value={newCustomer.phone}
                          onChange={(e) =>
                            setNewCustomer((c) => ({ ...c, phone: e.target.value }))
                          }
                          placeholder="(813) 555-0142"
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Email">
                        <input
                          type="email"
                          value={newCustomer.email}
                          onChange={(e) =>
                            setNewCustomer((c) => ({ ...c, email: e.target.value }))
                          }
                          placeholder="sarah@example.com"
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Company">
                        <input
                          value={newCustomer.company_name}
                          onChange={(e) =>
                            setNewCustomer((c) => ({ ...c, company_name: e.target.value }))
                          }
                          placeholder="Tampa Bay Golf Club"
                          className={inputCls}
                        />
                      </Field>
                    </div>
                    {!newCustomer.email.trim() && (
                      <p className="text-xs text-birdie-mustard-dark">
                        Without an email you can still build and print this quote, but you
                        won't be able to send it for online signature.
                      </p>
                    )}
                    <Button onClick={handleCreateCustomer} disabled={creatingCustomer}>
                      {creatingCustomer ? <Spinner label="Creating..." /> : "Create customer"}
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      value={customerQuery}
                      onChange={(e) => setCustomerQuery(e.target.value)}
                      placeholder="Search by name, email, phone, or company"
                      className={inputCls}
                      autoFocus
                    />
                    {searching && (
                      <div className="absolute right-3 top-2.5 text-xs text-birdie-charcoal/50">
                        searching...
                      </div>
                    )}
                    {customerResults.length > 0 && (
                      <div className="mt-2 border border-birdie-green/20 rounded-xl overflow-hidden divide-y divide-birdie-green/10 max-h-56 overflow-y-auto">
                        {customerResults.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => {
                              setCustomer(c);
                              setCustomerResults([]);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-birdie-cream transition"
                          >
                            <div className="font-medium text-birdie-charcoal text-sm">
                              {c.full_name}
                            </div>
                            <div className="text-xs text-birdie-charcoal/60">
                              {[c.email, c.phone, c.company_name].filter(Boolean).join(" · ")}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {!searching && customerQuery.trim().length >= 2 && customerResults.length === 0 && (
                      <p className="mt-2 text-sm text-birdie-charcoal/60">
                        No matches for "{customerQuery.trim()}".
                      </p>
                    )}

                    <button
                      type="button"
                      onClick={openNewCustomerForm}
                      className="mt-2 text-sm font-medium text-birdie-green hover:text-birdie-green-dark"
                    >
                      + New customer
                    </button>
                  </div>
                )}
              </Section>

              <Section title="Event">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Package">
                    <select
                      value={
                        selectedPackage
                          ? String(selectedPackage.id)
                          : customTitle || form.title
                          ? "custom"
                          : ""
                      }
                      onChange={(e) => {
                        if (e.target.value === "custom") {
                          setCustomTitle(true);
                          return;
                        }
                        const pkg = packages.find((p) => String(p.id) === e.target.value);
                        if (pkg) choosePackage(pkg);
                      }}
                      className={inputCls}
                      disabled={readOnly}
                    >
                      <option value="" disabled>
                        Choose a package…
                      </option>
                      {packages.map((p) => (
                        <option key={p.id} value={String(p.id)}>
                          {p.name} · {formatUSD(p.price_cents)}
                        </option>
                      ))}
                      <option value="custom">Custom title…</option>
                    </select>
                    {!selectedPackage && (customTitle || form.title) && (
                      <input
                        value={form.title}
                        onChange={(e) => setField("title", e.target.value)}
                        placeholder="Corporate Outing — Fall Classic"
                        className={`${inputCls} mt-2`}
                        disabled={readOnly}
                      />
                    )}
                  </Field>
                  <Field label="Event start">
                    <DateTimeField
                      value={form.event_date}
                      disabled={readOnly}
                      onChange={(start) =>
                        setForm((f) => ({
                          ...f,
                          event_date: start,
                          // A package fixes the length; otherwise fill an end
                          // time only if the owner hasn't set one yet.
                          event_end: selectedPackage
                            ? addHoursToLocal(start, selectedPackage.duration_minutes / 60)
                            : f.event_end
                            ? f.event_end
                            : addHoursToLocal(start, DEFAULT_EVENT_HOURS),
                        }))
                      }
                    />
                  </Field>
                  <Field label="Event end">
                    <DateTimeField
                      value={form.event_end}
                      disabled={readOnly}
                      onChange={(end) => setField("event_end", end)}
                    />
                    <span className="block text-xs text-birdie-charcoal/50 mt-1">
                      {selectedPackage
                        ? `Set by the package: ${selectedPackage.duration_minutes / 60} hours after start.`
                        : `Sets the calendar hold. Defaults to ${DEFAULT_EVENT_HOURS}h after start.`}
                    </span>
                  </Field>
                  <Field label="Location" className="sm:col-span-2">
                    {readOnly ? (
                      <input value={form.event_location} className={inputCls} disabled />
                    ) : (
                      <VenueAutocomplete
                        value={form.event_location}
                        onChange={(v) => setField("event_location", v)}
                        placeholder="Start typing a venue or address…"
                        className={inputCls}
                      />
                    )}
                  </Field>
                </div>
              </Section>

              <Section
                title="Line items"
                action={
                  !readOnly && services.some((s) => s.service_type !== "package") ? (
                    <select
                      value=""
                      onChange={(e) => {
                        const svc = services.find((s) => String(s.id) === e.target.value);
                        if (!svc) return;
                        addItem({
                          description: svc.name,
                          quantity: 1,
                          unit_price: inputFromCents(
                            svc.pricing_mode === "per_unit"
                              ? svc.price_per_unit_cents
                              : svc.price_cents
                          ),
                          unit_label: svc.pricing_mode === "per_unit" ? svc.unit_label || "" : "",
                        });
                        e.target.value = "";
                      }}
                      className="text-sm border border-birdie-green/25 rounded-lg px-2 py-1.5 bg-white"
                    >
                      <option value="">+ Add from services...</option>
                      {services
                        .filter((s) => s.service_type !== "package")
                        .map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                    </select>
                  ) : null
                }
              >
                <div className="space-y-2">
                  <div className="hidden sm:grid grid-cols-12 gap-2 text-xs font-medium text-birdie-charcoal/60 px-1">
                    <div className="col-span-8">Description</div>
                    <div className="col-span-3 text-right">Price</div>
                    <div className="col-span-1"></div>
                  </div>

                  {items.map((item, idx) => {
                    const amount = Math.round(
                      (Number(item.quantity) || 0) * centsFromInput(item.unit_price)
                    );
                    return (
                      <div
                        key={idx}
                        className="grid grid-cols-12 gap-2 items-start bg-white border border-birdie-green/15 rounded-xl p-2"
                      >
                        <div className="col-span-12 sm:col-span-8">
                          <input
                            value={item.description}
                            onChange={(e) => updateItem(idx, { description: e.target.value })}
                            placeholder="Extra hour, travel fee, custom package…"
                            className={inputCls}
                            disabled={readOnly}
                          />
                          <label className="flex items-center gap-1.5 mt-1.5 text-xs text-birdie-charcoal/60 px-1">
                            <input
                              type="checkbox"
                              checked={item.taxable !== false}
                              onChange={(e) => updateItem(idx, { taxable: e.target.checked })}
                              disabled={readOnly}
                              className="rounded border-birdie-green/30"
                            />
                            Taxable
                          </label>
                        </div>
                        <div className="col-span-11 sm:col-span-3">
                          <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-birdie-charcoal/50 text-sm">
                              $
                            </span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              inputMode="decimal"
                              value={item.unit_price}
                              onChange={(e) => updateItem(idx, { unit_price: e.target.value })}
                              placeholder="0.00"
                              className={`${inputCls} pl-6 text-right`}
                              disabled={readOnly}
                              aria-label="Price"
                            />
                          </div>
                          {/* Older quotes may carry a quantity; show it rather than hide the math. */}
                          {(Number(item.quantity) !== 1 || item.unit_label) && (
                            <div className="text-right text-xs text-birdie-charcoal/60 mt-1 pr-1">
                              × {Number(item.quantity)} {item.unit_label} = {formatUSD(amount)}
                            </div>
                          )}
                        </div>
                        <div className="col-span-1 flex justify-end">
                          {!readOnly && (
                            <button
                              type="button"
                              onClick={() => removeItem(idx)}
                              className="text-birdie-charcoal/40 hover:text-red-600 transition p-1.5"
                              aria-label="Remove line item"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => addItem()}
                    className="mt-3 text-sm font-medium text-birdie-green hover:text-birdie-green-dark"
                  >
                    + Add line item
                  </button>
                )}
              </Section>

              <Section title="Notes & terms">
                <div className="space-y-3">
                  <Field label="Notes (shown on the quote)">
                    {!readOnly && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {NOTE_BLOCKS.map((b) => {
                          const added = hasNoteBlock(form.notes, b);
                          return (
                            <button
                              key={b.key}
                              type="button"
                              disabled={added}
                              onClick={() => setField("notes", addNoteBlock(form.notes, b))}
                              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${
                                added
                                  ? "border-birdie-green/20 bg-birdie-green/10 text-birdie-green"
                                  : "border-birdie-green/25 text-birdie-green hover:bg-birdie-cream"
                              }`}
                            >
                              {added ? "✓ " : "+ "}
                              {b.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <textarea
                      value={form.notes}
                      onChange={(e) => setField("notes", e.target.value)}
                      rows={form.notes ? 8 : 2}
                      placeholder="Tap a block above, or write your own."
                      className={inputCls}
                      disabled={readOnly}
                    />
                  </Field>
                  <Field label="Custom terms (leave blank for the standard terms)">
                    <textarea
                      value={form.terms}
                      onChange={(e) => setField("terms", e.target.value)}
                      rows={2}
                      className={inputCls}
                      disabled={readOnly}
                    />
                  </Field>
                </div>
              </Section>
            </div>

            {/* Right: pricing summary */}
            <div className="space-y-4">
              <div className="bg-gradient-to-b from-birdie-cream to-birdie-beige rounded-2xl p-5 lg:sticky lg:top-2">
                <h3 className="font-accent text-lg font-bold text-birdie-charcoal mb-4">
                  Quote total
                </h3>

                <div className="space-y-2 text-sm">
                  <Row label="Subtotal" value={formatUSD(totals.subtotal)} />
                  {totals.discount > 0 && (
                    <Row label="Discount" value={`-${formatUSD(totals.discount)}`} tone="danger" />
                  )}
                  <Row
                    label={
                      form.tax_county
                        ? `Estimated sales tax (${form.tax_county})`
                        : "Estimated sales tax"
                    }
                    value={formatUSD(totals.tax)}
                  />
                  {totals.creditApplied > 0 && (
                    <Row label="Rain credit" value={`-${formatUSD(totals.creditApplied)}`} tone="danger" />
                  )}
                  <div className="border-t-2 border-birdie-mustard pt-2 mt-2 flex justify-between items-center">
                    <span className="font-bold text-birdie-charcoal">Total</span>
                    <span className="text-xl font-bold text-birdie-green">
                      {formatUSD(totals.total)}
                    </span>
                  </div>
                </div>

                {(credits.length > 0 || form.credit_id) && (
                  <div className="mt-4 rounded-xl border border-birdie-green/25 bg-white p-3 text-sm">
                    {credits.length === 0 ? (
                      <div className="text-birdie-charcoal/60 text-xs">
                        The credit on this quote is no longer available.{" "}
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={() => setField("credit_id", null)}
                            className="font-semibold text-birdie-green"
                          >
                            Remove it
                          </button>
                        )}
                      </div>
                    ) : (
                      credits.map((c) => {
                        const on = form.credit_id === c.id;
                        return (
                          <div key={c.id} className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="font-semibold text-birdie-charcoal">
                                {formatUSD(c.available_cents)} rain credit
                              </div>
                              <div className="text-xs text-birdie-charcoal/55 truncate">
                                {c.source_quote_number ? `From ${c.source_quote_number}` : c.reason}
                              </div>
                            </div>
                            {!readOnly && (
                              <button
                                type="button"
                                onClick={() => setField("credit_id", on ? null : c.id)}
                                className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                                  on
                                    ? "border border-birdie-green/25 text-birdie-charcoal/70"
                                    : "bg-birdie-green text-white"
                                }`}
                              >
                                {on ? "Remove" : "Apply"}
                              </button>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                <div className="mt-5 space-y-3">
                  <Field label="Discount ($)">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.discount}
                      onChange={(e) => setField("discount", e.target.value)}
                      placeholder="0.00"
                      className={inputCls}
                      disabled={readOnly}
                    />
                  </Field>
                  {centsFromInput(form.discount) > 0 && (
                    <Field label="Discount reason">
                      <input
                        value={form.discount_reason}
                        onChange={(e) => setField("discount_reason", e.target.value)}
                        placeholder="repeat customer"
                        className={inputCls}
                        disabled={readOnly}
                      />
                    </Field>
                  )}
                  <Field label="Deposit to reserve ($)">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.deposit}
                      onChange={(e) => setField("deposit", e.target.value)}
                      placeholder={`defaults to 50% (${formatUSD(
                        Math.floor(totals.total / 2)
                      )})`}
                      className={inputCls}
                      disabled={readOnly}
                    />
                    <span className="block text-xs text-birdie-charcoal/50 mt-1">
                      The pay page offers this or the full amount. Set it to the
                      total to require payment in full.
                    </span>
                  </Field>
                  <Field label="Valid until">
                    <input
                      type="date"
                      value={form.valid_until}
                      onChange={(e) => setField("valid_until", e.target.value)}
                      className={inputCls}
                      disabled={readOnly}
                    />
                  </Field>
                </div>

                <div className="mt-4 pt-4 border-t border-birdie-green/15">
                  <TaxCountyPicker
                    address={form.event_location}
                    county={form.tax_county}
                    rateBasisPoints={form.tax_rate_basis_points}
                    onChange={handleTaxChange}
                  />
                </div>
              </div>

              {!readOnly && (
                <PaymentCard
                  payment={payment}
                  saved={Boolean(savedId)}
                  onGenerate={handleGeneratePaymentLink}
                />
              )}
            </div>
          </div>

          <p className="text-xs text-birdie-charcoal/50 pt-2">
            <strong>Save</strong> pencils the date on your calendar and tracks the job —
            nothing is emailed. <strong>Save &amp; send</strong> also emails the customer
            the PDF and their payment link.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-birdie-green/15">
            <Button variant="secondary" onClick={() => onClose?.(false)} className="sm:w-auto">
              Cancel
            </Button>
            <div className="flex-1" />
            {!readOnly && (
              <>
                <Button variant="secondary" onClick={handlePreview} disabled={saving}>
                  Preview PDF
                </Button>
                <Button variant="secondary" onClick={handleSaveDraft} disabled={saving}>
                  Save
                </Button>
                <Button onClick={handleSend} disabled={saving}>
                  {saving ? <Spinner label="Saving..." /> : "Save & send"}
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

const inputCls =
  "w-full px-3 py-2 text-sm border border-birdie-green/25 rounded-lg bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-birdie-green/40 disabled:bg-birdie-beige/60 disabled:text-birdie-charcoal/60";

function Section({ title, action, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-accent text-base font-semibold text-birdie-charcoal">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-medium text-birdie-charcoal/70 mb-1">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value, tone }) {
  return (
    <div className="flex justify-between">
      <span className="text-birdie-charcoal/70">{label}</span>
      <span className={tone === "danger" ? "text-red-600" : "text-birdie-charcoal"}>{value}</span>
    </div>
  );
}

// Date picker + a quarter-hour time dropdown, so a time is always :00/:15/:30/:45.
// Reads/writes a `datetime-local` string ("YYYY-MM-DDTHH:mm").
function DateTimeField({ value, onChange, disabled }) {
  const { date, time } = splitLocal(value);
  // Keep a legacy off-grid time selectable rather than showing it blank.
  const hasTime = time && TIME_OPTIONS.some((o) => o.value === time);
  return (
    <div className="flex gap-2">
      <input
        type="date"
        value={date}
        disabled={disabled}
        onChange={(e) => onChange(combineLocal(e.target.value, time))}
        className={`${inputCls} flex-1`}
      />
      <select
        value={time}
        disabled={disabled}
        onChange={(e) => onChange(combineLocal(date, e.target.value))}
        className={`${inputCls} w-28`}
      >
        <option value="">Time…</option>
        {!hasTime && time && <option value={time}>{time}</option>}
        {TIME_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function PaymentCard({ payment, saved, onGenerate }) {
  const [copied, setCopied] = useState("");
  const payPageUrl = payment.publicToken
    ? `${window.location.origin}/pay/${payment.publicToken}`
    : "";
  const isPaid = payment.status === "paid";

  const copy = async (label, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(""), 1500);
    } catch {
      /* clipboard blocked; ignore */
    }
  };

  return (
    <div className="bg-white border border-birdie-green/15 rounded-2xl p-5">
      <h3 className="font-accent text-base font-semibold text-birdie-charcoal mb-1">Payment</h3>
      {isPaid ? (
        <div className="rounded-xl border border-birdie-green/25 bg-birdie-green/10 p-3 text-sm text-birdie-green font-medium">
          ✅ Paid — this job is confirmed.
        </div>
      ) : (
        <>
          <p className="text-xs text-birdie-charcoal/60 mb-3">
            Generate a Square link and share the pay page with your customer. Paying it
            confirms the job automatically.
          </p>
          <Button onClick={onGenerate} disabled={payment.working} className="w-full">
            {payment.working
              ? <Spinner label="Working..." />
              : payment.squareUrl
              ? "Refresh Square link"
              : saved
              ? "Create Square payment link"
              : "Save & create payment link"}
          </Button>

          {payPageUrl && payment.squareUrl && (
            <div className="mt-3 space-y-2">
              <LinkRow
                label="Customer pay page"
                url={payPageUrl}
                copied={copied === "page"}
                onCopy={() => copy("page", payPageUrl)}
              />
              <LinkRow
                label="Square link (direct)"
                url={payment.squareUrl}
                copied={copied === "square"}
                onCopy={() => copy("square", payment.squareUrl)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function LinkRow({ label, url, copied, onCopy }) {
  return (
    <div>
      <div className="text-[11px] font-medium text-birdie-charcoal/60 mb-0.5">{label}</div>
      <div className="flex gap-2">
        <input
          readOnly
          value={url}
          onFocus={(e) => e.target.select()}
          className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-birdie-green/20 rounded-lg bg-birdie-cream/50 text-birdie-charcoal/80"
        />
        <button
          type="button"
          onClick={onCopy}
          className="text-xs font-medium px-2.5 rounded-lg border border-birdie-green/25 bg-white hover:bg-birdie-cream text-birdie-green whitespace-nowrap"
        >
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
    </div>
  );
}
